
import React from 'react';
import { ArrowRight, Check, TrendingUp, Users, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const SellerJourneySection: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/contact');
  };
  
  const sellingSteps = language === 'en' ? [
    {
      number: "01",
      title: "Discovery Call",
      description: "A friendly conversation where we get to know each other, explore your business's story and operations, and see if Noa could be a great fit for your business.",
      timeLabel: "TIME IT TAKES",
      duration: "45 minutes"
    },
    {
      number: "02",
      title: "Personalized Evaluation",
      description: "We work with you to understand your business thoroughly and determine a fair valuation based on your unique goals.",
      timeLabel: "TIME IT TAKES",
      duration: "2 - 3 weeks"
    },
    {
      number: "03",
      title: "Due Diligence",
      description: "A detailed review process to ensure a smooth and structured transition with complete transparency.",
      timeLabel: "TIME IT TAKES",
      duration: "3 months"
    },
    {
      number: "04",
      title: "Long-term Partnership",
      description: "We design an agreement that respects your original vision and establish a plan for the sustainable growth of the business.",
      timeLabel: "TIME IT TAKES",
      duration: "Ongoing"
    }
  ] : [
    {
      number: "01",
      title: "Llamada de Descubrimiento",
      description: "Una conversación amistosa para conocernos mutuamente y explorar si Noa podría ser una gran opción para tu empresa.",
      timeLabel: "TIEMPO REQUERIDO",
      duration: "45 minutos"
    },
    {
      number: "02",
      title: "Evaluación personalizada",
      description: "Trabajamos contigo para entender tu negocio a fondo y determinar una valoración justa basada en tus objetivos únicos.",
      timeLabel: "TIEMPO REQUERIDO",
      duration: "2 - 3 semanas"
    },
    {
      number: "03",
      title: "Due diligence",
      description: "Un proceso detallado de revisión para asegurar una transición fluida y estructurada con total transparencia.",
      timeLabel: "TIEMPO REQUERIDO",
      duration: "3 meses"
    },
    {
      number: "04",
      title: "Asociación a largo plazo",
      description: "Diseñamos un acuerdo que respeta tu visión original y establecemos un plan para el crecimiento sostenible del negocio.",
      timeLabel: "TIEMPO REQUERIDO",
      duration: "Continuo"
    }
  ];

  // Define icons for the steps - this avoids the TypeScript error
  const stepIcons = [
    <TrendingUp className="w-6 h-6 text-white" />,
    <Users className="w-6 h-6 text-white" />,
    <Check className="w-6 h-6 text-white" />,
    <Heart className="w-6 h-6 text-white" />
  ];

  return (
    <section className="py-28 bg-[#f6f6f7] dark:bg-noa-charcoal">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 mb-5 bg-white dark:bg-green-500/10 rounded-full">
            <span className="text-sm font-medium text-[#586F7C] dark:text-green-500">
              {language === 'en' ? 'Your Journey' : 'Tu Proceso'}
            </span>
          </div>
          
          <h2 className="font-space-grotesk text-4xl md:text-5xl font-bold mb-6">
            {language === 'en' ? 'A Simple & Transparent Process' : 'Un Proceso Simple y Transparente'}
            <span className="text-blue-600">.</span>
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Our acquisition process is straightforward, transparent, and tailored to your unique situation.' 
              : 'Nuestro proceso de adquisición es directo, transparente y adaptado a tu situación única.'}
          </p>
        </div>

        <div className="relative">
          {/* Horizontal steps */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {sellingSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Step box */}
                <div className="bg-white dark:bg-noa-rich-black/80 rounded-lg shadow-md p-6 h-full flex flex-col relative z-10">
                  {/* Step number with icon */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center shadow-md">
                      {stepIcons[index]}
                    </div>
                    <span className="text-sm font-medium text-blue-600">
                      {language === 'en' ? `Step ${step.number}` : `Paso ${step.number}`}
                    </span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-space-grotesk text-xl font-bold text-[#0D1F2D] dark:text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-[#403E43] dark:text-[#F6F6F7]/70 text-sm">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow connector for all but the last item */}
                  {index < sellingSteps.length - 1 && (
                    <div className="hidden md:flex absolute -right-3 top-1/2 transform -translate-y-1/2 z-20">
                      <div className="bg-blue-500 rounded-full p-1">
                        <ArrowRight className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="mt-16 flex justify-center">
          <Button 
            variant="default" 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleContactClick}
          >
            {language === 'en' ? 'Start the conversation' : 'Iniciar la conversación'}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SellerJourneySection;
